import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaDownload, FaFileExcel } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";

// Columns whose blank cells can be filled from the dialog (the rest are per-lead data).
const DEFAULTABLE = new Set([
  "business_type",
  "business_model",
  "country_code",
  "project",
  "service",
  "requirement_type",
  "property_type",
  "site_visit",
  "status",
  "source",
]);

const STATUS_LABELS = { ready: "Ready", imported: "Imported", error: "Error", duplicate: "Duplicate" };

function apiError(err, fallback) {
  return err?.response?.data?.message || err?.response?.data?.error || fallback;
}

/**
 * Import leads from the company's Excel template (or any .xlsx / .csv with matching headers).
 * Step 1 previews every row (nothing saved); step 2 saves the valid rows.
 */
function LeadImportModal({ open, onClose, onImported }) {
  const [columns, setColumns] = useState([]);
  const [business, setBusiness] = useState({});
  const [defaults, setDefaults] = useState({});
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [problemsOnly, setProblemsOnly] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setFile(null);
    setPreview(null);
    setResult(null);
    setError("");
    axiosInstance
      .get("/client/leads/import/columns")
      .then((res) => {
        if (!active) return;
        const data = res.data?.data || {};
        const cols = Array.isArray(data.columns) ? data.columns : [];
        setColumns(cols);
        setBusiness(data);
        setDefaults(Object.fromEntries(cols.filter((c) => DEFAULTABLE.has(c.key)).map((c) => [c.key, c.default || ""])));
      })
      .catch((err) => active && setError(apiError(err, "Could not load the import columns.")));
    return () => {
      active = false;
    };
  }, [open]);

  const defaultCols = useMemo(() => columns.filter((c) => DEFAULTABLE.has(c.key)), [columns]);

  if (!open) return null;

  const downloadTemplate = async () => {
    setBusy("template");
    setError("");
    try {
      const res = await axiosInstance.get("/client/leads/import/template", { responseType: "blob", timeout: 60000 });
      const disposition = res.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : "leads-import-template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError("Could not download the template.");
    } finally {
      setBusy("");
    }
  };

  const send = async (dryRun) => {
    if (!file) {
      setError("Choose the filled template (.xlsx) or a .csv file first.");
      return;
    }
    setBusy(dryRun ? "preview" : "import");
    setError("");
    const body = new FormData();
    body.append("file", file);
    body.append("defaults", JSON.stringify(defaults));
    body.append("dry_run", dryRun ? "true" : "false");
    try {
      const res = await axiosInstance.post("/client/leads/import", body, { timeout: 120000 });
      const data = res.data?.data;
      if (dryRun) {
        setPreview(data);
        setResult(null);
      } else {
        setResult(data);
        setPreview(null);
        if (data?.counts?.imported) onImported?.(data);
      }
    } catch (err) {
      setError(apiError(err, dryRun ? "Could not read the file." : "Could not import leads."));
    } finally {
      setBusy("");
    }
  };

  const report = result || preview;
  const rows = (report?.rows || []).filter((r) => !problemsOnly || r.status === "error" || r.status === "duplicate");
  const counts = report?.counts || {};
  const readyCount = preview?.counts?.ready || 0;

  return (
    <div className="cportal-modal-overlay" onClick={busy ? undefined : onClose} role="presentation">
      <div
        className="cportal-modal cportal-modal--wide"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-import-title"
      >
        <div className="cportal-modal-header">
          <h3 id="lead-import-title">Import leads from Excel</h3>
          <button type="button" onClick={onClose} aria-label="Close" disabled={!!busy}>
            ✕
          </button>
        </div>
        <div className="cportal-modal-body cportal-import">
          {error ? <div className="cportal-alert cportal-alert-error">{error}</div> : null}

          <section className="cportal-import__step">
            <div className="cportal-import__step-head">
              <span className="cportal-import__num">1</span>
              <div>
                <strong>Download your template</strong>
                <p>
                  Columns match your business
                  {business.businessType ? ` (${business.businessType}${business.businessModel ? `, ${business.businessModel} based` : ""})` : ""}.
                  Required columns are red; defaults are pre-filled and dropdowns list your statuses, sources and
                  {business.businessModel === "Project" ? " projects" : " services"}.
                </p>
              </div>
            </div>
            <button type="button" className="cportal-btn cportal-btn-ghost" onClick={downloadTemplate} disabled={!!busy}>
              <FaDownload aria-hidden="true" /> {busy === "template" ? "Preparing…" : "Download template"}
            </button>
            {columns.length ? (
              <div className="cportal-import__cols">
                {columns.map((c) => (
                  <span key={c.key} className={`cportal-import__col${c.required ? " is-required" : ""}`} title={c.note || ""}>
                    {c.header}
                    {c.required ? " *" : ""}
                  </span>
                ))}
              </div>
            ) : null}
          </section>

          <section className="cportal-import__step">
            <div className="cportal-import__step-head">
              <span className="cportal-import__num">2</span>
              <div>
                <strong>Choose the filled file</strong>
                <p>.xlsx (template) or .csv with the same headers, up to 2000 leads.</p>
              </div>
            </div>
            <button
              type="button"
              className="cportal-import__drop"
              onClick={() => inputRef.current?.click()}
              disabled={!!busy}
            >
              <FaFileExcel aria-hidden="true" />
              {file ? file.name : "Click to choose a file"}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xlsm,.csv"
              hidden
              onChange={(e) => {
                setFile(e.target.files?.[0] || null);
                setPreview(null);
                setResult(null);
                e.target.value = "";
              }}
            />
          </section>

          {defaultCols.length ? (
            <section className="cportal-import__step">
              <div className="cportal-import__step-head">
                <span className="cportal-import__num">3</span>
                <div>
                  <strong>Defaults for blank cells</strong>
                  <p>Used only where a row leaves the column empty.</p>
                </div>
              </div>
              <div className="cportal-import__defaults">
                {defaultCols.map((c) => (
                  <label key={c.key} className="cportal-field">
                    <span>{c.header}</span>
                    {c.choices?.length ? (
                      <select
                        value={defaults[c.key] || ""}
                        onChange={(e) => setDefaults((d) => ({ ...d, [c.key]: e.target.value }))}
                      >
                        <option value="">— None —</option>
                        {c.choices.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        value={defaults[c.key] || ""}
                        onChange={(e) => setDefaults((d) => ({ ...d, [c.key]: e.target.value }))}
                      />
                    )}
                  </label>
                ))}
              </div>
            </section>
          ) : null}

          {report ? (
            <section className="cportal-import__report">
              <div className="cportal-import__counts">
                <span>{counts.total || 0} rows</span>
                {result ? (
                  <span className="is-ok">{counts.imported || 0} imported</span>
                ) : (
                  <span className="is-ok">{counts.ready || 0} ready</span>
                )}
                <span className="is-bad">{counts.errors || 0} errors</span>
                <span className="is-warn">{counts.duplicates || 0} duplicates</span>
                <label className="cportal-import__filter">
                  <input type="checkbox" checked={problemsOnly} onChange={(e) => setProblemsOnly(e.target.checked)} />
                  Only problems
                </label>
              </div>
              {report.ignored_columns?.length ? (
                <p className="cportal-import__note">Ignored columns: {report.ignored_columns.join(", ")}</p>
              ) : null}
              <div className="cportal-table-wrap cportal-import__table">
                <table className="cportal-table">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Name</th>
                      <th>Mobile</th>
                      <th>Result</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.row}>
                        <td>{r.row}</td>
                        <td>{r.name || "—"}</td>
                        <td>{r.mobile || "—"}</td>
                        <td>
                          <span className={`cportal-import__badge is-${r.status}`}>{STATUS_LABELS[r.status] || r.status}</span>
                        </td>
                        <td>{r.message || r.offering || ""}</td>
                      </tr>
                    ))}
                    {!rows.length ? (
                      <tr>
                        <td colSpan={5}>No rows to show.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          <div className="cportal-form-actions">
            <button type="button" className="cportal-btn cportal-btn-ghost" onClick={onClose} disabled={!!busy}>
              {result ? "Done" : "Cancel"}
            </button>
            {!result ? (
              <button type="button" className="cportal-btn cportal-btn-ghost" onClick={() => send(true)} disabled={!!busy || !file}>
                {busy === "preview" ? "Checking…" : preview ? "Check again" : "Preview"}
              </button>
            ) : null}
            {preview && !result ? (
              <button
                type="button"
                className="cportal-btn cportal-btn-primary"
                onClick={() => send(false)}
                disabled={!!busy || !readyCount}
              >
                {busy === "import" ? "Importing…" : `Import ${readyCount} lead${readyCount === 1 ? "" : "s"}`}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LeadImportModal;
